import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

import FilterModal from '../Modal/FilterModal';
import AskQuestionModal from '../Modal/AskQuestionModal';
import {
  BaseButton,
  Accordion,
  SearchInput,
  useAuth,
  useLoginRequiredModal,
  useT,
} from '@repo/common';

import { ReactComponent as FilterIcon } from '@repo/common/assets/icons/24px/icon_24px_filter.svg';

import { getQnaCategories } from '../../../api/qna/qnaCategory.api';
import { getSearchQna } from '../../../api/qna/qnaSearch.api';
import { getActiveTopicsWithFaqs } from '../../../api/faq/faq.api';

const SCROLL_THRESHOLD = 120;

const QnaSide = ({
  showCategory = true,
  qnaDetail = false,
  activeCategory,
  setActiveCategory,
  filterState,
  setFilterState,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { showLoginModal } = useLoginRequiredModal();
  const containerRef = useRef(null);
  const filterRef = useRef(null);
  const [modalCoords, setModalCoords] = useState({ top: 0, left: 0 });
  const filterBtnRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [faqItems, setFaqItems] = useState([]);
  const [sideSearchTerm, setSideSearchTerm] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isAskModalOpen, setIsAskModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const collapsed = !qnaDetail && !scrolled;

  const openAskModal = () => {
    if (!isLoggedIn) {
      showLoginModal('qna_side_ask_cta');
      return;
    }
    setIsAskModalOpen(true);
  };
  const closeAskModal = () => setIsAskModalOpen(false);

  const toggleFilter = () => {
    if (!isFilterModalOpen && filterBtnRef.current) {
      const rect = filterBtnRef.current.getBoundingClientRect();
      setModalCoords({ top: rect.bottom, left: rect.left });
    }
    setIsFilterModalOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isClickInsideFilterBtn =
        filterRef.current && filterRef.current.contains(e.target);
      const isClickInsideModal = e.target.closest('.filter-modal-container');
      if (!isClickInsideFilterBtn && !isClickInsideModal) {
        setIsFilterModalOpen(false);
      }
    };
    if (isFilterModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFilterModalOpen]);

  useEffect(() => {
    if (!showCategory) return;
    const fetchCategories = async () => {
      const data = await getQnaCategories({ includeAll: true });
      setCategories(data);
    };
    fetchCategories();
  }, [showCategory]);

  useEffect(() => {
    const fetchActiveFaqs = async () => {
      try {
        const topics = await getActiveTopicsWithFaqs();
        const items = topics.flatMap((topic) =>
          topic.faqs.map((faq) => ({
            question: faq.question,
            slug: topic.slug,
            faqId: faq.id,
          })),
        );
        setFaqItems(items);
      } catch (error) {
        console.error('Failed to fetch active FAQs:', error);
      }
    };
    fetchActiveFaqs();
  }, []);

  const handleCategoryClick = (categoryId) => setActiveCategory(categoryId);
  const handleFaqClick = (slug, faqId) => {
    navigate('/faq', { state: { targetSlug: slug, targetFaqId: faqId } });
  };

  const handleSearch = async () => {
    if (!sideSearchTerm.trim()) return;
    try {
      const result = await getSearchQna({ keyword: sideSearchTerm });
      navigate(
        `/community/qna/search?keyword=${encodeURIComponent(sideSearchTerm)}`,
        { state: { searchResult: result } },
      );
    } catch (err) {
      console.error('검색 실패', err);
    }
  };

  return (
    <div
      ref={containerRef}
      className={clsx(
        'sticky top-[106px] self-start ml-auto w-full max-w-[270px] flex flex-col gap-[18px]',
        scrolled && 'pt-[104px]',
      )}
    >
      {collapsed ? (
        <BaseButton
          size='l'
          iconPosition='after'
          width='100%'
          onClick={openAskModal}
          textWrapperClass='flex-none'
        >
          {t('Ask your question', '질문을 남겨보세요')}
        </BaseButton>
      ) : (
        <div className='p-4 bg-grayscale-gray00 border-[1.6px] border-grayscale-gray02 rounded-[15px]'>
          <div className='flex items-center justify-between gap-2 mb-6 typo-title3'>
            Q&A
            {!qnaDetail && (
              <div
                ref={filterRef}
                className='relative w-10 h-10 flex justify-center items-center'
              >
                <button
                  ref={filterBtnRef}
                  type='button'
                  onClick={toggleFilter}
                  className={clsx(
                    'w-10 h-10 p-2 flex items-center justify-center rounded-[8px] transition-colors',
                    'hover:text-green-deep03',
                    isFilterModalOpen &&
                      'bg-functional-success01 text-green-deep03',
                  )}
                >
                  <FilterIcon />
                </button>
                {isFilterModalOpen && (
                  <FilterModal
                    className='bg-functional-success01'
                    onClose={() => setIsFilterModalOpen(false)}
                    coords={modalCoords}
                    initialSort={filterState.sortBy}
                    initialChecked={filterState.onlyAnswered}
                    initialUnansweredChecked={filterState.onlyUnanswered}
                    onApply={(newFilters) =>
                      setFilterState({
                        sortBy: newFilters.sortBy,
                        onlyAnswered: newFilters.checked,
                        onlyUnanswered: newFilters.unansweredChecked,
                      })
                    }
                  />
                )}
              </div>
            )}
          </div>

          <div className='pb-2'>
            <BaseButton
              size='l'
              iconPosition='after'
              width='100%'
              onClick={openAskModal}
              textWrapperClass='flex-none'
            >
              {t('Ask now', '지금 질문하기')}
            </BaseButton>
          </div>

          <div
            className={clsx('flex gap-2', showCategory ? 'mb-[30px]' : 'mb-0')}
          >
            <div className='relative flex-grow'>
              <SearchInput
                value={sideSearchTerm}
                placeholder={t('Search questions', '질문 검색')}
                activePlaceholder={t('Search questions', '질문 검색')}
                onChange={setSideSearchTerm}
                onEnter={handleSearch}
              />
            </div>
          </div>

          {showCategory && (
            <>
              <div className='typo-title3 text-grayscale-gray09 mb-2'>
                {t('Category', '카테고리')}
              </div>
              <nav className='flex flex-col'>
                {categories.map((category) => {
                  const isAdmin = category.id === 7;
                  const isActive = activeCategory === category.id;
                  const textClass = isAdmin
                    ? isActive
                      ? 'text-functional-accent03'
                      : 'text-grayscale-gray07'
                    : isActive
                      ? 'text-green-deep03'
                      : 'text-grayscale-gray07';
                  return (
                    <BaseButton
                      key={category.id}
                      type='text'
                      size='l'
                      hideIcon
                      onClick={() => handleCategoryClick(category.id)}
                      bgclass='bg-grayscale-gray00 border-grayscale-gray00 p-3 h-[40px]'
                      textclass={`justify-start ${textClass}`}
                      separate={true}
                      leftalign={true}
                      hoverclass='hover:bg-grayscale-gray00'
                      activeclass='active:bg-grayscale-gray00'
                    >
                      {category.name}
                    </BaseButton>
                  );
                })}
              </nav>
            </>
          )}
        </div>
      )}

      <AskQuestionModal isOpen={isAskModalOpen} onClose={closeAskModal} />

      <Accordion
        items={faqItems.map((item) => (
          <span
            key={item.faqId}
            onClick={(e) => {
              e.stopPropagation();
              handleFaqClick(item.slug, item.faqId);
            }}
            className='w-full block'
          >
            {item.question}
          </span>
        ))}
        onItemClick={(index) =>
          handleFaqClick(faqItems[index]?.slug, faqItems[index]?.faqId)
        }
        defaultOpen={collapsed}
        isOpen={qnaDetail || collapsed}
      />
    </div>
  );
};

export default QnaSide;
